import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import * as THREE from "three";
import gsap from "gsap";
import { useSceneStore } from "@/state/useSceneStore";
import { useProgressStore } from "@/state/useProgressStore";

const ROT_SPEED = 0.005;
const INERTIA_DECAY = 0.94;
const DRAG_ARM_PX = 5;
const ALIGN_THRESHOLD = Math.cos((10 * Math.PI) / 180);
const HOLD_MS = 1000;
const TARGET_UP = new THREE.Vector3(0, 1, 0);

export const Cube = (): JSX.Element | null => {
  const mesh = useSceneStore((s) => s.objects.cube);
  const focusedObject = useSceneStore((s) => s.focusedObject);
  const focused = focusedObject === "cube";
  const { gl } = useThree();
  const cubeSolved = useProgressStore((s) => s.unlocks.cubeSolved);
  const unlock = useProgressStore((s) => s.unlock);

  const spin = useRef({ x: 0, y: 0 });
  const dragging = useRef<boolean>(false);
  const lastPointer = useRef<{ x: number; y: number } | null>(null);
  const totalDragPx = useRef<number>(0);
  const hasDragged = useRef<boolean>(false);
  const alignStartedAt = useRef<number | null>(null);
  const tmpVec = useRef<THREE.Vector3>(new THREE.Vector3());

  const [holding, setHolding] = useState<boolean>(false);
  const [justSolved, setJustSolved] = useState<boolean>(false);

  useEffect(() => {
    if (!mesh || !focused) return;
    totalDragPx.current = 0;
    hasDragged.current = false;
    alignStartedAt.current = null;
    window.setTimeout(() => setHolding(false), 0);
    const tween = gsap.to(mesh.rotation, {
      x: 0,
      y: 0,
      z: 0,
      duration: 0.6,
      ease: "power2.out",
    });
    return () => {
      tween.kill();
    };
  }, [mesh, focused]);

  useEffect(() => {
    if (!focused || !mesh) return;
    const canvas = gl.domElement;

    const onDown = (e: PointerEvent): void => {
      dragging.current = true;
      lastPointer.current = { x: e.clientX, y: e.clientY };
      canvas.setPointerCapture(e.pointerId);
    };

    const onMove = (e: PointerEvent): void => {
      if (!dragging.current || !lastPointer.current) return;
      const dx = e.clientX - lastPointer.current.x;
      const dy = e.clientY - lastPointer.current.y;
      mesh.rotation.y += dx * ROT_SPEED;
      mesh.rotation.x += dy * ROT_SPEED;
      spin.current.y = dx * ROT_SPEED;
      spin.current.x = dy * ROT_SPEED;
      lastPointer.current = { x: e.clientX, y: e.clientY };
      totalDragPx.current += Math.abs(dx) + Math.abs(dy);
      if (totalDragPx.current >= DRAG_ARM_PX) hasDragged.current = true;
    };

    const onUp = (e: PointerEvent): void => {
      dragging.current = false;
      lastPointer.current = null;
      if (canvas.hasPointerCapture(e.pointerId)) {
        canvas.releasePointerCapture(e.pointerId);
      }
    };

    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerup", onUp);
    canvas.addEventListener("pointercancel", onUp);

    return () => {
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerup", onUp);
      canvas.removeEventListener("pointercancel", onUp);
    };
  }, [focused, mesh, gl]);

  useFrame(() => {
    if (!mesh || !focused) return;

    if (!dragging.current) {
      mesh.rotation.x += spin.current.x;
      mesh.rotation.y += spin.current.y;
      spin.current.x *= INERTIA_DECAY;
      spin.current.y *= INERTIA_DECAY;
    }

    if (cubeSolved || !hasDragged.current) {
      if (holding) setHolding(false);
      alignStartedAt.current = null;
      return;
    }

    tmpVec.current.set(0, 1, 0).applyQuaternion(mesh.quaternion);
    const dot = tmpVec.current.dot(TARGET_UP);

    if (dot >= ALIGN_THRESHOLD) {
      if (alignStartedAt.current === null) {
        alignStartedAt.current = performance.now();
        if (!holding) setHolding(true);
      } else if (performance.now() - alignStartedAt.current >= HOLD_MS) {
        unlock("cubeSolved");
        setHolding(false);
        setJustSolved(true);
        alignStartedAt.current = null;
        window.setTimeout(() => setJustSolved(false), 2000);
      }
    } else {
      alignStartedAt.current = null;
      if (holding) setHolding(false);
    }
  });

  if (!mesh) return null;

  const showBadge = focused && (holding || justSolved);
  if (!showBadge) return null;

  const worldPos = new THREE.Vector3();
  mesh.getWorldPosition(worldPos);

  return (
    <Html
      position={[worldPos.x, worldPos.y + 0.15, worldPos.z]}
      center
      zIndexRange={[10, 0]}
    >
      <div className="pointer-events-none rounded-sm border border-phosphor bg-bg1/90 px-3 py-1 font-mono text-xs uppercase tracking-wider text-phosphor">
        {justSolved ? "solved" : "hold it..."}
      </div>
    </Html>
  );
};
